import React, { useState } from 'react';
import { useOnboarding } from '../../context/OnboardingContext';
import { X, ArrowRight } from 'lucide-react';

const TABS = [
  { id: 'flow', label: '📋 Flujo Principal' },
  { id: 'roles', label: '👥 Usuarios y Roles' },
  { id: 'evals', label: '📊 Evaluaciones' },
  { id: 'map', label: '🗺️ Mapa y Evidencia' },
  { id: 'reports', label: '📄 Reportes' },
];

function FlowNode({ icon, label, desc, color }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative flex flex-col items-center cursor-pointer" onClick={() => setShow(!show)}>
      <div className={`w-16 h-16 rounded-sm flex flex-col items-center justify-center gap-0.5 border-2 transition-all hover:scale-105 ${show ? 'shadow-lg' : ''}`}
        style={{ borderColor: color, backgroundColor: show ? color : 'white' }}>
        <span className="text-2xl">{icon}</span>
        <p className="text-xs font-bold" style={{ color: show ? 'white' : color }}>{label}</p>
      </div>
      {show && (
        <div className="absolute top-full mt-2 w-40 bg-soem-navy text-white text-xs p-2.5 rounded-sm shadow-xl z-10">
          {desc}
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-2 h-2 bg-soem-navy rotate-45 mt-0.5" />
        </div>
      )}
    </div>
  );
}

export default function QuickGuide() {
  const { showQuickGuide, setShowQuickGuide } = useOnboarding();
  const [activeTab, setActiveTab] = useState('flow');

  if (!showQuickGuide) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-soem-navy rounded-t-sm flex-shrink-0">
          <h2 className="text-white font-bold">Guía rápida de SOEM</h2>
          <button aria-label="Cerrar guía rápida" onClick={() => setShowQuickGuide(false)}><X size={18} className="text-slate-400 hover:text-white" /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 flex-shrink-0 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2.5 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors ${activeTab === t.id ? 'border-soem-primary text-soem-primary' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* Flow Principal */}
          {activeTab === 'flow' && (
            <div className="space-y-6">
              <p className="text-slate-500 text-sm">Haz clic en cada elemento para ver más detalles del flujo de trabajo.</p>
              <div className="flex items-center gap-3 flex-wrap justify-center">
                {[
                  { icon: '🏢', label: 'Cliente', color: '#D97706', desc: 'Empresa o promotor que contrata el servicio SOEM. Cada proyecto se vincula a un cliente.' },
                  { icon: '📁', label: 'Proyecto', color: '#1A56DB', desc: 'Agrupa cliente, eventos, evaluaciones y equipo. Es el eje central.' },
                  { icon: '📅', label: 'Evento', color: '#7C3AED', desc: 'El evento específico: concierto, deportivo, corporativo. Vinculado a locación y proyecto.' },
                  { icon: '📍', label: 'Locación', color: '#DC2626', desc: 'El recinto físico: estadio, auditorio, explanada. Tiene mapa y datos de capacidad.' },
                  { icon: '📋', label: 'Evaluación', color: '#15803D', desc: '35 criterios de seguridad. El levantador la hace en campo, el líder la aprueba.' },
                  { icon: '📄', label: 'Reporte oficial', color: '#0D9488',
                    desc: 'Un PDF con folio, resumen ejecutivo y detalle técnico. Se genera al publicar.' },
                ].map((node, i) => (
                  <React.Fragment key={node.label}>
                    <FlowNode {...node} />
                    {i < 5 && <ArrowRight size={20} className="text-slate-300 flex-shrink-0" />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* Roles */}
          {activeTab === 'roles' && (
            <div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { role: 'Super Admin', emoji: '👑', color: '#7C3AED', perms: ['Control total del sistema', 'CRUD de usuarios, clientes y proyectos', 'Audit log y modo espejo', 'Constructor de plantillas'] },
                  { role: 'Líder de Proyecto', emoji: '📋', color: '#1A56DB', perms: ['Crea y gestiona proyectos', 'Asigna levantadores', 'Aprueba o regresa evaluaciones', 'Genera reportes PDF'] },
                  { role: 'Levantador', emoji: '🏟', color: '#15803D', perms: ['Realiza checklists en campo', 'Sube evidencia fotográfica', 'Agrega marcadores al mapa', 'Solo ve sus proyectos asignados'] },
                  { role: 'Cliente', emoji: '🏢', color: '#D97706', perms: [
                    'Solo lectura de su empresa', 'Ve evaluaciones publicadas',
                    'Descarga reportes oficiales', 'Consulta bitácora de incidentes',
                  ] },
                ].map(r => (
                  <div key={r.role} className="border-2 rounded-sm p-4" style={{ borderColor: r.color }}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-2xl">{r.emoji}</span>
                      <h3 className="font-bold text-soem-navy">{r.role}</h3>
                    </div>
                    <ul className="space-y-1">
                      {r.perms.map(p => (
                        <li key={p} className="text-xs text-slate-600 flex items-start gap-1.5">
                          <span style={{ color: r.color }} className="mt-0.5 flex-shrink-0">✓</span> {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Evaluaciones */}
          {activeTab === 'evals' && (
            <div className="space-y-5">
              <div>
                <p className="soem-label mb-3">Flujo de estados</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {[
                    { status: 'Borrador', color: '#94a3b8', desc: 'El levantador está llenando el formulario' },
                    { status: 'En Revisión', color: '#1A56DB', desc: 'El líder está revisando' },
                    { status: 'Regresada', color: '#D97706', desc: 'El líder pidió correcciones' },
                    { status: 'Publicada', color: '#15803D', desc: 'Aprobada y congelada' },
                  ].map((s, i) => (
                    <React.Fragment key={s.status}>
                      <div className="flex flex-col items-center">
                        <div className="px-3 py-1.5 rounded-full text-white text-xs font-bold" style={{ backgroundColor: s.color }}>{s.status}</div>
                        <p className="text-xs text-slate-400 mt-1 text-center max-w-24">{s.desc}</p>
                      </div>
                      {i < 3 && <ArrowRight size={16} className="text-slate-300 mb-4" />}
                    </React.Fragment>
                  ))}
                </div>
              </div>
              <div>
                <p className="soem-label mb-2">Escala de calificación</p>
                <div className="flex gap-1.5">
                  {[[0,'#DC2626','Crítico'],[2,'#D97706',''],[4,'#D97706',''],[6,'#CA8A04',''],[8,'#15803D',''],[10,'#15803D','Excelente']].map(([v,c,l]) => (
                    <div key={v} className="flex flex-col items-center">
                      <div className="w-9 h-9 rounded-sm flex items-center justify-center text-white text-sm font-black" style={{ backgroundColor: c }}>{v}</div>
                      {l && <p className="text-xs text-slate-400 mt-0.5">{l}</p>}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="soem-label mb-2">Criterios Bloqueantes (score 0 impide publicar)</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {['Permisos y Licencias','Plan de Seguridad','Plan de Evacuación','Extintores','Servicio Médico','Rutas de Evacuación'].map(c => (
                    <div key={c} className="flex items-center gap-1.5 text-xs text-red-700 bg-red-50 border border-red-200 px-2 py-1.5 rounded-sm">
                      <span className="font-bold">⚠</span> {c}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Mapa y Evidencia */}
          {activeTab === 'map' && (
            <div className="space-y-5">
              <div>
                <p className="soem-label mb-3">Tipos de marcadores en el mapa</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { type: 'Hospital', emoji: 'H', color: '#15803D', desc: 'Hospitales y clínicas cercanas al recinto' },
                    { type: 'Ruta Evacuación', emoji: 'E', color: '#1A56DB', desc: 'Rutas de escape en caso de emergencia' },
                    { type: 'Zona de Riesgo', emoji: 'R', color: '#D97706', desc: 'Áreas de riesgo identificadas en la inspección' },
                    { type: 'Punto Crítico', emoji: '!', color: '#DC2626', desc: 'Puntos críticos de seguridad o acceso' },
                  ].map(m => (
                    <div key={m.type} className="flex items-start gap-3 p-3 bg-slate-50 rounded-sm border border-slate-200">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-black flex-shrink-0" style={{ backgroundColor: m.color }}>{m.emoji}</div>
                      <div>
                        <p className="font-semibold text-soem-navy text-sm">{m.type}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{m.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="soem-label mb-2">Tipos de evidencia aceptados</p>
                <div className="flex gap-3">
                  {[['📷', 'Fotos', 'JPG, PNG, WebP'], ['🎥', 'Videos', 'MP4, MOV'], ['🎤', 'Notas de voz', 'MP3, WAV, M4A']].map(([e, l, f]) => (
                    <div key={l} className="flex-1 bg-slate-50 border border-slate-200 rounded-sm p-3 text-center">
                      <span className="text-2xl block mb-1">{e}</span>
                      <p className="text-xs font-semibold text-soem-navy">{l}</p>
                      <p className="text-xs text-slate-400">{f}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Reportes */}
          {activeTab === 'reports' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="border-2 border-soem-primary rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">📊</span>
                    <h3 className="font-bold text-soem-navy">Reporte oficial</h3>
                  </div>
                  <ul className="text-xs text-slate-600 space-y-1.5">
                    <li>✓ Folio y resumen ejecutivo</li>
                    <li>✓ Resultados y detalle técnico</li>
                    <li>✓ Observaciones y recomendaciones</li>
                    <li>✓ Hallazgos con su cadena y evidencias</li>
                  </ul>
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <p className="text-xs text-slate-400">
                      <strong>Para:</strong> Usuarios con acceso, incluido el cliente
                    </p>
                  </div>
                </div>
                <div className="border-2 border-soem-success rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">📄</span>
                    <h3 className="font-bold text-soem-navy">Reporte final del evento</h3>
                  </div>
                  <ul className="text-xs text-slate-600 space-y-1.5">
                    <li>✓ Resumen Antes, Durante y Después</li>
                    <li>✓ Cadenas completas de hallazgos</li>
                    <li>✓ Evidencia inicial, acciones y evidencia final</li>
                    <li>✓ Recomendaciones abiertas</li>
                    <li>✓ Botón en la pestaña Después</li>
                  </ul>
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <p className="text-xs text-slate-400"><strong>Para:</strong> Usuarios con acceso al evento</p>
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
                <p className="text-sm font-semibold text-blue-800 mb-1">¿Cuándo se generan?</p>
                <p className="text-xs text-blue-700">
                  Al publicar se genera el Reporte oficial con folio. Si sigue en generación, vuelve a consultar.
                  La copia vigente permanece disponible al archivar o regenerar, incluso si la regeneración falla.
                </p>
                <p className="text-xs text-blue-700 mt-2">
                  Antes de publicar, el líder y super_admin pueden abrir desde la revisión
                  {' '}<strong>Vista previa — BORRADOR (no oficial)</strong>.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
